import React, { useState, useContext, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MessagesSquare, Mail, X } from 'lucide-react';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import RejectDialog from './RejectDialog';
import CommunicationModal from './CommunicationModal';
import {
  visibleFooterButtons,
  ExecutionHeaderSlotContext,
  ExecutionDirtyContext,
} from './responseShellTypes';
import type { ResponseShellProps } from './responseShellTypes';
import {
  communicationsService,
  type CommMessage,
  type CommThread,
} from '../../../services/auditService';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

/**
 * Shared wrapper for every transactional response form (Checklist, Form,
 * Questionnaire, Rating, Confirmation, PhysicalCheck) and the approval UI.
 *
 * 05b polish (2026-05-19) — inline-style sprawl replaced with token-driven CSS
 * classes (.response-shell-*), bespoke btn helpers replaced with shared Button
 * component, autosave indicator now has visible dot, Submit gets the
 * CONTEXT D1 confirmation dialog that was missing.
 *
 * Phase 107 (2026-06-19) — D-1/D-2/D-3 shared-chrome rebuild:
 *   - D-1: single `Procedure: "<Name>" (<Type>)` heading owned here (ExecutionModal
 *          no longer duplicates it).
 *   - D-2: the context line splits into Section 1 (execution context + status)
 *          and Section 2 (action cluster + autosave). The bottom footer bar is
 *          removed; all verbs + autosave indicator + action-error live in
 *          Section 2, gated by the UNCHANGED visibleFooterButtons matrix.
 *   - D-3: a general/overall Evidence button lives in Section 2. There is NO
 *          instance-level evidence persistence endpoint today (only per-row
 *          evidence_file_id and finding-scoped evidence exist — see 107-02 Task 0
 *          discovery / RESEARCH A2), so the button ships EXPLICITLY DISABLED
 *          ("coming soon") rather than accepting bytes that would be silently
 *          dropped. Wiring it is a BACKEND DEPENDENCY for a future phase.
 */

const ResponseShell: React.FC<ResponseShellProps> = ({
  instance,
  // Maker = performer, Checker = approver. Rendered in the context meta row
  // (next to Start/Due) — the modal top bar now holds only the title + close.
  performerName,
  approverName,
  role,
  onSaveDraft,
  onSubmit,
  onApprove,
  onReject,
  onReopen,
  autosaveStatus = 'idle',
  rejectedCount = 0,
  incompleteReason = null,
  children,
}) => {
  const { formatDate } = useEntitySettings();
  const state = instance.workflow_state;
  const buttons = visibleFooterButtons(state, role);
  // G3 (phase 108) — an Approved instance is immutable in the UI. This derives
  // SOLELY from workflow_state === 'Approved' (NOT buttons.readOnly, which is the
  // role-based no-action flag that is also true for the 'other' viewer role). The
  // comm buttons below honour it; each response body derives the same approved
  // signal locally to disable its answer inputs / EvidenceCell / RaiseFindingButton.
  const approved = state === 'Approved';
  const [busyVerb, setBusyVerb] = useState<null | 'save' | 'submit' | 'approve' | 'reopen'>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [commChannel, setCommChannel] = useState<null | 'internal' | 'external'>(null);
  // Phase 113 — right-side comm drawer. Header icons toggle it open per channel;
  // the "Raise" button inside opens CommunicationModal (commChannel) for creation.
  const [drawerChannel, setDrawerChannel] = useState<null | 'internal' | 'external'>(null);

  const runAction = async (verb: 'save' | 'submit' | 'approve' | 'reopen', fn?: () => Promise<void>) => {
    if (!fn) return;
    setBusyVerb(verb);
    setActionError(null);
    try {
      await fn();
    } catch (e: any) {
      setActionError(e?.response?.data?.detail ?? e?.message ?? `Failed to ${verb}.`);
    } finally {
      setBusyVerb(null);
    }
  };

  const procedureName =
    (instance as any).audit_procedure_name ?? (instance as any).procedure_name ?? 'Procedure';

  // Execution context — what is being executed, against what, and by when.
  const inst = instance as any;
  // No sample attached → the procedure runs entity-wide; otherwise show the
  // sample item's name (fall back to a placeholder only if the name failed to load).
  const sampleLabel =
    inst.sample_item_id == null
      ? 'Entity wide'
      : (inst.sample_item_name ?? '—');
  const contextItems: Array<{ label: string; value: string }> = [
    { label: 'Program',    value: inst.program_name ?? '—' },
    { label: 'Population', value: inst.population_name ?? '—' },
    { label: 'Sample',     value: sampleLabel },
    { label: 'Start',      value: formatDate(inst.start_date) },
    { label: 'Due',        value: formatDate(instance.due_date) },
    // Maker (performer) / Checker (approver) now live here in the meta row,
    // beside the dates — moved out of the modal top header.
    { label: 'Maker',      value: performerName ?? '—' },
    { label: 'Checker',    value: approverName ?? '—' },
  ];

  // When hosted in ExecutionModal, this is the header DOM node (next to the close
  // button) that the action cluster portals into. Null elsewhere → render inline.
  const headerSlot = useContext(ExecutionHeaderSlotContext);

  // Report unsaved-changes state up so the host can warn before closing.
  // 'dirty' = edits not yet saved; 'failed' = a save attempt left them unsaved.
  const reportDirty = useContext(ExecutionDirtyContext);
  const isDirty = autosaveStatus === 'dirty' || autosaveStatus === 'failed';
  useEffect(() => {
    reportDirty?.(isDirty);
    return () => reportDirty?.(false);
  }, [isDirty, reportDirty]);

  // Phase 113 Task 4 — fetch instance-level comm threads + messages inline.
  // Renders as .response-evidence-block list below the body. No badge counts.
  const [commMessages, setCommMessages] = useState<Array<{ thread: CommThread; messages: CommMessage[] }>>([]);

  const loadComms = useCallback(async () => {
    try {
      const instanceId = instance.id;
      // Fetch all threads (internal + external) for this instance.
      const [internalResult, externalResult] = await Promise.all([
        communicationsService.list({ channel: 'internal', page: 1, limit: 500 }),
        communicationsService.list({ channel: 'external', page: 1, limit: 500 }),
      ]);
      const allThreads = [
        ...(internalResult.items ?? []),
        ...(externalResult.items ?? []),
      ].filter((t) => t.procedure_instance_id === instanceId);
      if (allThreads.length === 0) {
        setCommMessages([]);
        return;
      }
      // Fetch messages for each thread.
      const details = await Promise.all(
        allThreads.map(async (t) => {
          try {
            const detail = await communicationsService.get(t.id);
            return { thread: t, messages: detail.messages ?? [] };
          } catch {
            return { thread: t, messages: [] };
          }
        }),
      );
      setCommMessages(details);
    } catch {
      // Non-fatal — inline list renders empty.
    }
  }, [instance.id]);

  useEffect(() => {
    void loadComms();
  }, [loadComms]);

  // Per-channel thread groups for the drawer + header badge dots.
  const internalThreads = commMessages.filter((c) => c.thread.channel === 'internal');
  const externalThreads = commMessages.filter((c) => c.thread.channel === 'external');
  const drawerThreads =
    drawerChannel === 'internal'
      ? internalThreads
      : drawerChannel === 'external'
      ? externalThreads
      : [];

  // Section-2 action cluster (comm icons left + status action button + checker verbs right).
  // Rendered once, then either portaled into the modal header or placed inline.
  // Fix 2 (phase 113): comm buttons are in a left group; status/verbs in a right group
  // separated by margin-left:auto so comms appear on the left and status on the right.
  const actionCluster = (
          <div className="response-shell-action-cluster">
            {/* LEFT: Internal + External communication buttons — toggle the drawer.
                A dot rides the icon when that channel already has threads. */}
            <span className="response-shell-comm-left">
              <span className={`response-shell-comm-btn-wrap${internalThreads.length > 0 ? ' has-threads' : ''}`}>
                <Button
                  variant={drawerChannel === 'internal' ? 'fill' : 'secondary-outline'}
                  icon={MessagesSquare}
                  onClick={() => setDrawerChannel((c) => (c === 'internal' ? null : 'internal'))}
                  title="Internal Communication"
                  aria-label="Internal Communication"
                  aria-pressed={drawerChannel === 'internal'}
                  data-testid="BTN_response_shell_internal_comm"
                />
              </span>
              <span className={`response-shell-comm-btn-wrap${externalThreads.length > 0 ? ' has-threads' : ''}`}>
                <Button
                  variant={drawerChannel === 'external' ? 'fill' : 'secondary-outline'}
                  icon={Mail}
                  onClick={() => setDrawerChannel((c) => (c === 'external' ? null : 'external'))}
                  title="External Communication"
                  aria-label="External Communication"
                  aria-pressed={drawerChannel === 'external'}
                  data-testid="BTN_response_shell_external_comm"
                />
              </span>
            </span>

            {/* RIGHT: error, rejected badge, status/verbs — pushed to right via margin-left:auto */}
            <span className="response-shell-status-right">
              <span className="response-shell-autosave-slot">
                {actionError && <span className="response-shell-action-error">{actionError}</span>}
              </span>

              {/* G2 (phase 108) — maker-visible count of lines a checker rejected.
                  Visible to everyone; it gates Approve below so the checker can't
                  approve while any line carries a reject note. */}
              {rejectedCount > 0 && (
                <span
                  className="status-badge sm danger response-shell-rejected-badge"
                  title="Lines a checker has rejected. Clear every reject note to enable Approve."
                  data-testid="LBL_response_shell_rejected_count"
                >
                  {rejectedCount} rejected
                </span>
              )}

              {/* D-3 procedure-wide evidence button removed (2026-06-19): there is no
                  instance-level evidence persistence endpoint yet, so it only ever
                  shipped disabled. Re-add when the backend storage lands. */}

              {/* Maker actionable (Draft/Reopened/InProgress): one status split-button.
                  Fix 3 (phase 113): caption now reflects live save status (Saving…/Unsaved/Saved).
                  The menu carries the two save modes; no visible chevron. */}
              {buttons.saveDraft ? (
                <StatusSplitButton
                  status={state}
                  busyVerb={busyVerb}
                  autosaveStatus={autosaveStatus}
                  onSaveDraft={() => runAction('save', onSaveDraft)}
                  onSubmit={() => setSubmitConfirmOpen(true)}
                  disabled={busyVerb !== null}
                  submitBlockedReason={incompleteReason}
                />
              ) : approved ? (
                /* Approved is terminal — no Approve button; show a green "Approved"
                   label so the outcome is unmistakable. */
                <span
                  className="response-shell-status-chip response-shell-status-chip--approved"
                  data-testid="LBL_response_shell_approved"
                  title="Procedure approved"
                >
                  ✓ Approved
                </span>
              ) : (
                /* Not maker-actionable — show status as a plain (disabled) chip so the
                   procedure state is always visible somewhere in the action cluster.
                   Checker/lead verbs (Approve/Send Back/Reopen) render alongside it. */
                <span
                  className="response-shell-status-chip"
                  data-testid="LBL_response_shell_status"
                  title="Procedure status"
                >
                  {state}
                </span>
              )}
              {buttons.reject && (
                <Button
                  variant="delete-outline"
                  data-testid="BTN_response_shell_reject"
                  onClick={() => setRejectOpen(true)}
                  disabled={busyVerb !== null || !onReject}
                >
                  Send Back
                </Button>
              )}
              {buttons.approve && (
                <Button
                  variant="fill"
                  data-testid="BTN_response_shell_approve"
                  onClick={() => runAction('approve', onApprove)}
                  // G2 (phase 108) — block Approve while any line is rejected.
                  disabled={busyVerb !== null || !onApprove || rejectedCount > 0}
                  title={
                    rejectedCount > 0
                      ? `Cannot approve — ${rejectedCount} line(s) rejected. Send back or clear the reject notes first.`
                      : undefined
                  }
                >
                  {busyVerb === 'approve' ? 'Approving…' : 'Approve'}
                </Button>
              )}
              {buttons.reopen && (
                <Button
                  variant="secondary-outline"
                  data-testid="BTN_response_shell_reopen"
                  onClick={() => runAction('reopen', onReopen)}
                  disabled={busyVerb !== null || !onReopen}
                >
                  {busyVerb === 'reopen' ? 'Reopening…' : 'Reopen'}
                </Button>
              )}
            </span>
          </div>
  );

  return (
    <div className="response-shell-root">
      <header className="response-shell-header">
        {/* D-1 — the `Procedure: "<Name>" (<Type>)` heading renders in the
            ExecutionModal top bar (single title surface); not duplicated here. */}
        <div className="response-shell-headline">
          {/* Section 1 (left) — execution context meta strip (incl. Maker/Checker). */}
          <div className="response-shell-context" data-testid="response_shell_context">
            {contextItems.map(({ label, value }) => (
              <span key={label} className="response-shell-meta-item">
                <span className="response-shell-meta-label">{label}: </span>
                <span className="response-shell-meta-value">{value}</span>
              </span>
            ))}
          </div>

          {/* No header slot (shell used outside ExecutionModal) → keep the action
              cluster inline on the right with its divider. Otherwise it portals
              into the modal header, next to the close button. */}
          {!headerSlot && (
            <>
              <div className="response-shell-headline-divider" aria-hidden="true" />
              {actionCluster}
            </>
          )}
        </div>
      </header>

      {headerSlot && createPortal(actionCluster, headerSlot)}

      <div className="response-shell-body-scroll">
        {children}
      </div>

      {/* Phase 113 — instance-level communications live in a right-side drawer,
          toggled by the header internal/external icons. Keeps the answering area
          clean; comms surface on demand. */}
      {drawerChannel && (
        <>
          <div
            className="response-comm-drawer-overlay"
            aria-hidden="true"
            onClick={() => setDrawerChannel(null)}
          />
          <aside
            className="response-comm-drawer"
            role="dialog"
            aria-label={drawerChannel === 'internal' ? 'Internal Communication' : 'External Communication'}
            data-testid="PNL_response_shell_comm_drawer"
          >
            <header className="response-comm-drawer__header">
              <span className="response-comm-drawer__title">
                {drawerChannel === 'internal' ? 'Internal Communication' : 'External Communication'}
              </span>
              <button
                type="button"
                className="response-comm-drawer__close"
                aria-label="Close"
                onClick={() => setDrawerChannel(null)}
              >
                <X size={18} />
              </button>
            </header>

            <div className="response-comm-drawer__body">
              {drawerThreads.length === 0 ? (
                <div className="response-comm-drawer__empty">No communications yet.</div>
              ) : (
                drawerThreads.map(({ thread, messages }) => (
                  <div key={thread.id} className="response-comms-thread">
                    <div className="response-comms-thread__subject">{thread.subject}</div>
                    {messages.map((msg) => (
                      <div key={msg.id} className="response-evidence-block__item">
                        {msg.sender_name ?? `User ${msg.sender_user_id}`}
                        {msg.created_on && (
                          <span className="response-evidence-block__item-meta"> · {formatDate(msg.created_on)}</span>
                        )}
                        {' — '}
                        {msg.body}
                      </div>
                    ))}
                    {messages.length === 0 && (
                      <div className="response-evidence-block__item response-evidence-block__item--muted">
                        No messages yet.
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <footer className="response-comm-drawer__footer">
              <Button
                variant="fill"
                onClick={() => {
                  setCommChannel(drawerChannel);
                  setDrawerChannel(null);
                }}
                disabled={approved}
                title={approved ? 'Communications are locked after approval.' : undefined}
                data-testid="BTN_response_shell_comm_raise"
              >
                Raise {drawerChannel === 'internal' ? 'internal' : 'external'} communication
              </Button>
            </footer>
          </aside>
        </>
      )}

      {onReject && (
        <RejectDialog
          isOpen={rejectOpen}
          onClose={() => setRejectOpen(false)}
          onConfirm={async (comment) => {
            await onReject(comment);
          }}
        />
      )}

      {/* CONTEXT D1 contract — Submit confirmation (was missing per 05b-UI-REVIEW). */}
      {submitConfirmOpen && (
        <SubmitConfirmDialog
          onCancel={() => setSubmitConfirmOpen(false)}
          onConfirm={async () => {
            setSubmitConfirmOpen(false);
            await runAction('submit', onSubmit);
          }}
        />
      )}

      <CommunicationModal
        isOpen={commChannel !== null}
        channel={commChannel ?? 'internal'}
        procedureInstanceId={(instance as any).id}
        procedureName={procedureName}
        programInstanceId={(instance as any).audit_program_instance_id ?? null}
        onClose={() => setCommChannel(null)}
        onCreated={loadComms}
      />
    </div>
  );
};

/**
 * Maker action control — a single button whose caption is the live workflow
 * status (e.g. "Draft", "Reopened"). Clicking opens a two-item menu:
 *   • Save & submit for review  → onSubmit (the body's onSubmit already flushes
 *                                  unsaved answers before transitioning)
 *   • Save, don't submit        → onSaveDraft (flush only, stay in Draft)
 * Replaces the old separate Save Draft + Submit for Review buttons and the
 * removed standalone status pill — status now lives on the button itself.
 */
const StatusSplitButton: React.FC<{
  status: string;
  busyVerb: null | 'save' | 'submit' | 'approve' | 'reopen';
  autosaveStatus?: import('./responseShellTypes').AutosaveStatus;
  onSaveDraft: () => void;
  onSubmit: () => void;
  disabled: boolean;
  /** Non-empty => Submit is blocked; the string explains why (unanswered questions). */
  submitBlockedReason?: string | null;
}> = ({ status, busyVerb, autosaveStatus = 'idle', onSaveDraft, onSubmit, disabled, submitBlockedReason = null }) => {
  const [open, setOpen] = useState(false);

  // Fix 3 (phase 113): caption reflects live save state, no chevron.
  const caption: string =
    busyVerb === 'save'
      ? 'Saving…'
      : busyVerb === 'submit'
      ? 'Submitting…'
      : autosaveStatus === 'dirty' || autosaveStatus === 'failed'
      ? `${status} · Unsaved`
      : autosaveStatus === 'saved'
      ? `${status} · Saved`
      : status;

  const pick = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <span className="response-shell-split">
      <Button
        variant="fill"
        data-testid="BTN_response_shell_status_action"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="response-shell-split-caption">{caption}</span>
      </Button>
      {open && (
        <>
          {/* click-outside catcher */}
          <div
            className="response-shell-split-backdrop"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div className="response-shell-split-menu" role="menu">
            <button
              type="button"
              role="menuitem"
              className="response-shell-split-item"
              data-testid="BTN_response_shell_save_draft"
              onClick={() => pick(onSaveDraft)}
            >
              Save, don&apos;t submit
            </button>
            <button
              type="button"
              role="menuitem"
              className="response-shell-split-item"
              data-testid="BTN_response_shell_save_submit"
              onClick={() => { if (!submitBlockedReason) pick(onSubmit); }}
              disabled={!!submitBlockedReason}
              aria-disabled={!!submitBlockedReason}
              title={submitBlockedReason ?? undefined}
            >
              Save &amp; submit for review
            </button>
            {submitBlockedReason && (
              <div
                className="response-shell-split-note"
                role="note"
                data-testid="LBL_response_shell_submit_blocked"
              >
                {submitBlockedReason}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
};

const SubmitConfirmDialog: React.FC<{
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}> = ({ onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal isOpen={true} onClose={onCancel} size="sm">
      <Modal.Header>Submit for review?</Modal.Header>
      <Modal.Body>
        <p>Submit this work for review? You won't be able to edit after submission.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary-outline" data-testid="BTN_response_shell_submit_confirm_cancel" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button variant="fill" data-testid="BTN_response_shell_submit_confirm" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Submitting...' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ResponseShell;
